import React from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

const DefaultButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;

    height: ${(props) => (props.size === 'normal' ? '44px' : '36px')};
    border-radius: 4px;
    outline: none;
    box-sizing: border-box;
    border: none;

    font-size: 14px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.16px;
    line-height: 20px;

    cursor: pointer;

    transition-duration: 0.25s;

    &:disabled {
        cursor: initial;
    }

    /** Fallback for browser without focus-visible */
    &:focus:not(:active) {
        box-shadow: var(--outline);
    }

    /** Remove mouse-focus for browser that support functionality */
    &:focus:not(:focus-visible):not(:active) {
        box-shadow: none;
    }

    &:focus-visible:not(:active) {
        box-shadow: var(--outline);
    }

    &::-moz-focus-inner,
    &::-moz-focus-outer {
        border: 0;
    }

    ${(props) =>
        props.fluid &&
        css`
            width: 100%;
        `}
`;

export const PrimaryButton = styled(DefaultButton)`
    padding: 0 16px;
    background-color: ${(props) => props.theme.colors['myzanichelli-blue-main']};
    color: ${(props) => props.theme.colors['text-white']};

    &:disabled {
        background-color: ${(props) => props.theme.colors['surface-grey-200']};
        color: ${(props) => props.theme.colors['text-grey-700']};
    }

    &:hover:not(:disabled):not(:active) {
        background-color: ${(props) => props.theme.colors['myzanichelli-blue-dark']};
    }

    &:active {
        background-color: ${(props) => props.theme.colors['myzanichelli-blue-light']};
        box-shadow: ${(props) => props.theme.shadows['shadow-02dp']};
    }
`;

export const SecondaryButton = styled(DefaultButton)`
    padding: 0 16px;
    border: 2px solid ${(props) => props.theme.colors['myzanichelli-blue-main']};
    background-color: ${(props) => props.theme.colors['surface-white']};
    color: ${(props) => props.theme.colors['text-myzanichelli-blue-main']};

    &:disabled {
        color: ${(props) => props.theme.colors['text-grey-700']};
        border-color: ${(props) => props.theme.colors['surface-grey-200']};
    }

    &:hover:not(:disabled):not(:active) {
        color: ${(props) => props.theme.colors['text-myzanichelli-blue-dark']};
        border-color: ${(props) => props.theme.colors['myzanichelli-blue-dark']};
    }

    &:active {
        color: ${(props) => props.theme.colors['text-myzanichelli-blue-light']};
        border-color: ${(props) => props.theme.colors['myzanichelli-blue-light']};
        box-shadow: ${(props) => props.theme.shadows['shadow-02dp']};
    }
`;

export const TertiaryButton = styled(DefaultButton)`
    padding: 0 8px;
    background-color: transparent;
    color: ${(props) => props.theme.colors['text-myzanichelli-blue-main']};

    &:disabled {
        color: ${(props) => props.theme.colors['text-grey-700']};
    }

    &:focus {
        background-color: ${(props) => props.theme.colors['surface-white']};
    }

    &:focus:not(:focus-visible):not(:active) {
        background-color: transparent;
    }

    &:hover:not(:disabled):not(:active) {
        background-color: ${(props) => props.theme.colors['myzanichelli-blue-lighter']};
        color: ${(props) => props.theme.colors['text-myzanichelli-blue-dark']};
    }

    &:active {
        background-color: ${(props) => props.theme.colors['surface-white']};
        color: ${(props) => props.theme.colors['text-myzanichelli-blue-light']};
        box-shadow: ${(props) => props.theme.shadows['shadow-02dp']};
    }
`;

const Button = ({ type = 'primary', children, size = 'normal', disabled = false, onClick = () => {}, fluid = false, ...rest }) => {
    switch (type) {
        case 'tertiary':
            return (
                <TertiaryButton type={type} size={size} disabled={disabled} onClick={onClick} fluid={fluid} {...rest}>
                    {children}
                </TertiaryButton>
            );
        case 'secondary':
            return (
                <SecondaryButton type={type} size={size} disabled={disabled} onClick={onClick} fluid={fluid} {...rest}>
                    {children}
                </SecondaryButton>
            );
        case 'primary':
        default:
            return (
                <PrimaryButton type={type} size={size} disabled={disabled} onClick={onClick} fluid={fluid} {...rest}>
                    {children}
                </PrimaryButton>
            );
    }
};

Button.propTypes = {
    children: PropTypes.node,
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
    size: PropTypes.oneOf(['normal', 'small']),
    fluid: PropTypes.bool,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
};

export default Button;
